import { addDays, format, isWeekend } from 'date-fns';
import { getTimezoneOffset } from 'date-fns-tz';

import type { TimeSlotType } from '@widget/api/types';

const BERLIN_TZ = 'Europe/Berlin';
const MAX_BUSINESS_DAYS = 3;
const DAY_START_HOUR = 8;
const DAY_END_HOUR = 17;

const SLOT_DURATION_MINUTES = 30;
const SLOT_BOOKING_BUFFER_MINUTES = 15;

function getLocalUtcOffset() {
  return new Date().getTimezoneOffset();
}

function getBerlinUtcOffset() {
  return getTimezoneOffset(BERLIN_TZ, new Date()) / 1000 / 60 / 60;
}

function getUTCDayStartHours() {
  return DAY_START_HOUR - getBerlinUtcOffset(); // 8am Berlin time in UTC, DST adjusted
}

function getUTCDayEndHours() {
  return DAY_END_HOUR - getBerlinUtcOffset(); // 17pm Berlin time in UTC, DST adjusted
}

function generateBusinessDays() {
  const businessDays = [];

  const localNow = new Date();

  const isEndOfDay =
    localNow.getUTCHours() >= getUTCDayEndHours() ||
    (localNow.getUTCHours() === getUTCDayEndHours() - 1 &&
      localNow.getUTCMinutes() >= SLOT_DURATION_MINUTES - SLOT_BOOKING_BUFFER_MINUTES);

  const utcYear = localNow.getUTCFullYear();
  const utcMonth = localNow.getUTCMonth();
  const utcDayOfMonth = localNow.getUTCDate();
  const utcDate = new Date(utcYear, utcMonth, utcDayOfMonth);

  const startFrom = isEndOfDay ? addDays(utcDate, 1) : utcDate;

  let dayIterator = startFrom;

  while (businessDays.length < MAX_BUSINESS_DAYS) {
    if (!isWeekend(dayIterator)) {
      businessDays.push(dayIterator);
    }
    dayIterator = addDays(dayIterator, 1);
  }

  return businessDays;
}

function generateSlotsForDay(day: Date) {
  const slots: TimeSlotType[] = [];

  const firstSlot = new Date(day).setHours(getUTCDayStartHours(), 0, 0, 0);
  const lastSlot = new Date(day).setHours(getUTCDayEndHours(), 0, 0, 0);

  let slotIterator = firstSlot;

  const utcNow = Date.now() + getLocalUtcOffset() * 60 * 1000;

  while (slotIterator < lastSlot) {
    if (slotIterator > utcNow + SLOT_BOOKING_BUFFER_MINUTES * 60 * 1000) {
      slots.push({
        isoDateTime: format(slotIterator, "yyyy-MM-dd'T'HH:mm:ss'Z'"),
        email: '',
        isOptimal: false,
      });
    }

    slotIterator += SLOT_DURATION_MINUTES * 60 * 1000;
  }

  return slots;
}

export function getSalesTimeslots(): Record<string, { slots: TimeSlotType[] }> {
  const businessDays = generateBusinessDays();

  const slotsPerDay = businessDays.reduce((acc, day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    const slots = generateSlotsForDay(day);
    acc[dateStr] = { slots };
    return acc;
  }, {} as Record<string, { slots: TimeSlotType[] }>);

  return slotsPerDay;
}
